<template>
  <transition
    name="highlight-notes-drawer-transition"
    enter-from-class="highlight-notes-drawer-transition--enter-from"
    enter-active-class="highlight-notes-drawer-transition--enter-active"
    leave-active-class="highlight-notes-drawer-transition--leave-active"
    leave-to-class="highlight-notes-drawer-transition--leave-to"
  >
    <div
      v-show="drawerOpen"
      class="editor-drawer editor-drawer--right highlight-notes-drawer"
      :class="drawerOpen && 'highlight-notes-drawer--open'"
    >
      <NotesPanel
        v-if="highlights"
        ref="notesPanel"
        :avatar-path="emptyNotesImage"
        :calculate-panel-height="false"
        :focused-highlight="focusedHighlight"
        :highlights="sortedHighlights"
        :note-token="noteToken"
        :notebook-link-url="notebookUrl"
        :concept-guid="referenceId('concept')"
        :course-guid="referenceId('course')"
        :unit-guid="referenceId('unit')"
        :buttonYellowAttributes="{
          'de-tracking-name': 'studio_highlight-note-list_yellow-highlight-button',
        }"
        :buttonPinkAttributes="{
          'de-tracking-name': 'studio_highlight-note-list_pink-highlight-button',
        }"
        :buttonGreenAttributes="{
          'de-tracking-name': 'studio_highlight-note-list_green-highlight-button',
        }"
        :buttonBlueAttributes="{
          'de-tracking-name': 'studio_highlight-note-list_blue-highlight-button',
        }"
        :buttonEditCancelAttributes="{
          'de-tracking-name': 'studio_highlight-notes_edit-cancel-button',
        }"
        :buttonEditSaveAttributes="{
          'de-tracking-name': 'studio_highlight-notes_edit-save-button',
        }"
        :buttonDeleteAttributes="{
          'de-tracking-name': 'studio_highlight-note-list_delete-button',
        }"
        :buttonEditAttributes="{
          'de-tracking-name': 'studio_highlight-note-list_edit-button',
        }"
        :buttonNotebookAttributes="{
          'de-tracking-name': 'studio_highlight-notes-sidebar_open-notebook-button',
        }"
        :buttonHideNotesAttributes="{
          'de-tracking-name': 'studio_highlight-notes-sidebar_hide-notes-button',
        }"
        :buttonDeleteNotesAttributes="{
          'de-tracking-name': 'studio_highlight-notes-sidebar_delete-all-button',
        }"
        @toggle-panel="toggleNotesPanel"
        @highlight-color-change="onHighlightColorChange"
        @highlight-selected="onHighlightNoteSelected"
        @note-created="onNoteCreated"
        @note-updated="onNoteUpdated"
        @note-deleted="onNoteDeleted"
        @note-text-changed="onNoteTextChanged"
        @dialog-closed="onDialogClosed"
        @edit-canceled="onActionConfirmed"
        @active-note-changed="onActiveNoteChanged"
      />
    </div>
  </transition>
</template>

<script>
import { find } from 'lodash-es';
import { mapState, mapActions } from 'pinia';
import { NotesPanel } from '@discoveryedu/platform-components';
import { bus } from '@/lib/eventBus';
import { getDomains } from '@/lib/api';
import Deferred from '@/lib/deferred';
import emptyNotesAvatar from '@/assets/hightlight-art-6-8.png';
import chain from '@/lib/utils/chain';
import * as types from '@/lib/constants/store';

import {
  useAppStore,
  useConfirmationStore,
  useEditorStore,
  useTextSelectStore,
} from '@/stores';

const UNSAVED_CHANGES_CONFIRM_ID = 'highlight-notes-unsaved-changes';
const BODY_OVERFLOW_CLASS = 'highlight-notes-drawer--overflow';
const EMPTY_HIGHLIGHT_FOCUSED_KEY = 'empty';

export default {
  name: 'HighlightNotesDrawer',
  components: {
    NotesPanel,
  },
  props: {
    offsetBottom: {
      type: String,
      default: '0px',
    },
    textSelectMetaData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      focusedHighlightId: null,
      emptyNotesImage: window.paths?.core_static ? null : emptyNotesAvatar,
      activeNote: null,
      activeNoteText: null,
    };
  },
  mounted() {
    bus.on('highlight-note:create', this.onCreateNote);
    bus.on('highlight-note:show', this.onShowNote);
    bus.on('highlight:selected', this.onHighlightSelected);
    bus.on('highlight:deselected', this.onHighlightDeselected);
    this.setNotesPanelVisible(this.drawerOpen);
    this.toggleBodyOverflow(this.drawerOpen);
  },
  beforeUnmount() {
    bus.off('highlight-note:create', this.onCreateNote);
    bus.off('highlight-note:show', this.onShowNote);
    bus.off('highlight:selected', this.onHighlightSelected);
    bus.off('highlight:deselected', this.onHighlightDeselected);
    this.toggleBodyOverflow(false);
  },
  watch: {
    drawerOpen() {
      if (!this.drawerOpen) {
        this.cancelEdit();
      }
      /*
        If the drawer was closed, wait for the animation
        to complete before hiding the notes panel.
      */
      const timeout = this.drawerOpen ? 0 : 250;
      if (this.notesPanelVisibleTimeout) {
        clearTimeout(this.notesPanelVisibleTimeout);
      }
      this.notesPanelVisibleTimeout = setTimeout(() => {
        this.setNotesPanelVisible(this.drawerOpen);
        this.notesPanelVisibleTimeout = null;
      }, timeout);
      this.toggleBodyOverflow(this.drawerOpen);
    },
    activeNoteHasChanges(hasChanges) {
      /*
        Whenever the active note has unsaved changes, then add a confirmation
        to the store to confirm any actions that would close the highlight
        notes drawer or navigate away.
      */
      if (hasChanges) {
        this.addCancelEditConfirmation();
      } else {
        this[types.REMOVE_CONFIRMATION](UNSAVED_CHANGES_CONFIRM_ID);
      }
    },
    currentPageId() {
      this.cancelEdit();
    },
    currentModalCanvasId() {
      if (this.currentModalCanvasId) {
        this.cancelEdit();
      }
    },
    $route() {
      this.cancelEdit();
    },
  },
  computed: {
    ...mapState(useAppStore, [
      'apiToken',
    ]),
    ...mapState(useConfirmationStore, [
      'confirmations',
    ]),
    ...mapState(useEditorStore, [
      'currentModalCanvasId',
      'currentPageId',
      'editorDrawers',
    ]),
    ...mapState(useTextSelectStore, [
      'highlights',
    ]),
    drawerOpen() {
      return !this.currentModalCanvasId && this.editorDrawers.highlightNotes;
    },
    noteToken() {
      return window.initData?.csrf_tokens?.notebook?.note || this.apiToken;
    },
    focusedHighlight() {
      if (this.focusedHighlightId === EMPTY_HIGHLIGHT_FOCUSED_KEY) {
        return EMPTY_HIGHLIGHT_FOCUSED_KEY;
      }
      return find(this.highlights, (h) => h.id === this.focusedHighlightId && h.note?.text)?.id;
    },
    sortedHighlights() {
      // Sort highlights by their position (top left) on the canvas.
      return chain(this.highlights)
        .map((highlight) => {
          const mark = this.getHighlightElement(highlight);
          const rect = mark?.getBoundingClientRect();
          return {
            highlight,
            x: rect?.x,
            y: rect?.y,
          };
        })
        .sortBy(['y', 'x'])
        .map((i) => i.highlight)
        .value();
    },
    notebookUrl() {
      const { appDomain } = getDomains();
      const course = this.referenceId('course');
      const unit = this.referenceId('unit');
      const concept = this.referenceId('concept');
      return `${appDomain}/notebook/course/${course}/unit/${unit}/concept/${concept}`;
    },
    confirmation() {
      return this[types.GET_CONFIRMATION](UNSAVED_CHANGES_CONFIRM_ID);
    },
    activeNoteHasChanges() {
      if (!this.activeNote) return false;
      return this.activeNote.text !== this.activeNoteText;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.TOGGLE_EDITOR_DRAWER,
    ]),
    ...mapActions(useConfirmationStore, [
      types.ADD_CONFIRMATION,
      types.GET_CONFIRMATION,
      types.REMOVE_CONFIRMATION,
    ]),
    setNotesPanelVisible(isVisible) {
      const { notesPanel } = this.$refs;
      if (notesPanel) {
        notesPanel.notesPanelVisible = isVisible;
      }
    },
    toggleBodyOverflow(hasClass) {
      /*
        Add a class to the body to unset 'overflow' styles added by the
        nebula panel component. This is needed for StudioView to display
        properly on very small screens or high level zoom.
      */
      document.body.classList[hasClass ? 'add' : 'remove'](BODY_OVERFLOW_CLASS);
    },
    referenceId(type) {
      return this.textSelectMetaData?.references
        .find((r) => r.reference_type === type)?.reference_id;
    },
    toggleNotesPanel(open) {
      this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'highlightNotes', open });
    },
    async onCreateNote({ highlight }) {
      this.focusedHighlightId = EMPTY_HIGHLIGHT_FOCUSED_KEY;
      if (!this.editorDrawers.highlightNotes) {
        await this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'highlightNotes', open: true });
      }
      const { notesPanel } = this.$refs;
      if (notesPanel) {
        notesPanel.createNote(highlight);
      }
      bus.emit('text-select-tools:close-tools');
    },
    async onShowNote({ note }) {
      if (!this.drawerOpen) {
        await this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'highlightNotes', open: true });
        this.focusedHighlightId = note.highlight.id;
        return;
      }
      this.focusedHighlightId = note.highlight.id;
      const { notesPanel } = this.$refs;
      if (notesPanel) {
        notesPanel.focusNote(note.highlight.id);
      }
    },
    onHighlightSelected(args) {
      if (this.focusedHighlightId === EMPTY_HIGHLIGHT_FOCUSED_KEY) return;
      const highlight = args?.data?.highlight;
      this.focusedHighlightId = highlight?.note?.id ? highlight.id : null;
    },
    onHighlightDeselected() {
      if (this.focusedHighlightId === EMPTY_HIGHLIGHT_FOCUSED_KEY) return;
      this.focusedHighlightId = null;
    },
    onHighlightColorChange(highlight) {
      bus.emit('highlight:color-change', highlight);
    },
    onNoteCreated(note) {
      bus.emit('note:changed', note);
    },
    onNoteUpdated(note) {
      bus.emit('note:changed', note);
    },
    onNoteDeleted(note) {
      // Delete was confirmed so resolve the confirmation.
      this.onActionConfirmed();
      if (note.id) {
        bus.emit('note:changed', note);
      }
    },
    onDialogClosed() {
      if (this.confirmation) {
        const { deferred } = this.confirmation;
        /*
          Reject and reset the confirmation. This means the
          user clicked 'cancel' in the confirmation dialog.
        */
        deferred.reject();
        deferred.reset();
      }
    },
    onActionConfirmed() {
      if (this.confirmation) {
        /*
          User confirmed the action from the dialog.
          Resolve and remove the confirmation from the store.
        */
        this.confirmation.deferred.resolve(true);
        this.clearConfirmation();
      }
    },
    onActiveNoteChanged({ activeNote }) {
      if (!activeNote) {
        this.clearActiveNote();
        return;
      }
      /*
        If we're entering edit mode, then set the local active note and
        initial active note text. We will track changes on the active note
        text until edit mode is exited.
      */
      this.activeNote = activeNote;
      this.activeNoteText = this.activeNote?.text;
    },
    onNoteTextChanged(noteText) {
      this.activeNoteText = noteText;
    },
    onHighlightNoteSelected(highlight) {
      const mark = this.getHighlightElement(highlight);
      if (!mark) return;
      mark.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    },
    getHighlightElement(highlight) {
      return document.querySelector(`[data-highlight-id="${highlight.id}"]`);
    },
    clearActiveNote() {
      // Ends our local edit mode tracking.
      this.activeNote = null;
      this.activeNoteText = null;
      /*
        If we finished adding a new note, then make sure the initial focus
        is not on the new note form since it will no longer be visible.
      */
      if (this.focusedHighlightId === EMPTY_HIGHLIGHT_FOCUSED_KEY) {
        this.focusedHighlightId = null;
      }
    },
    addCancelEditConfirmation() {
      /*
        Adds a confirmation to the store for any actions that
        would cause the notes panel to close while editing or
        navigate away from the current page.
      */
      const confirmation = {
        id: UNSAVED_CHANGES_CONFIRM_ID,
        actions: new Set([
          types.VIEW_PAGE_BY_ID,
          `${types.TOGGLE_EDITOR_DRAWER}:highlightNotes:close`,
          types.VIEW_MODAL_CANVAS_BY_ID,
          `requestFullscreen:${types.VIEW_PAGE_BY_ID}`,
          'history:back',
        ]),
        deferred: new Deferred(this.confirmCancelEdit),
      };
      this[types.ADD_CONFIRMATION](confirmation);
    },
    confirmCancelEdit() {
      /*
        This is the action called by the confirmation added to the store. This
        action will invoke the correct confirmation dialog from the NotesPanel based on
        whether we're adding, deleting or updating a note.
      */
      // If we're not in edit mode, exit.
      if (!this.activeNote) return;
      const { notesPanel } = this.$refs;
      if (notesPanel) {
        if (this.activeNote?.id) {
          // Show confirm dialog for canceling a note edit.
          notesPanel.onCancelEdit(this.activeNote, this.activeNoteText);
        } else {
          // Show dialog for confirming a note delete or canceling add note (same as delete).
          notesPanel.onNoteDelete(this.activeNote);
        }
      }
    },
    cancelEdit() {
      /*
        Cancels the current edit or add note form in the
        NotesPanel. This is only called to reset the NotesPanel
        when there are no oustanding unsaved changes. Otherwise,
        there will be a confirmation in the store that will invoke a
        confirmation dialog from the NotesPanel.
      */
      if (this.activeNote && !this.confirmation) {
        const { notesPanel } = this.$refs;
        if (notesPanel) {
          if (this.activeNote?.id) {
            notesPanel.confirmCancel(this.activeNote);
          } else {
            notesPanel.confirmDelete(this.activeNote);
          }
          notesPanel.reset();
        }
        this.clearActiveNote();
      }
    },
    clearConfirmation() {
      this[types.REMOVE_CONFIRMATION](UNSAVED_CHANGES_CONFIRM_ID);
    },
  },
};
</script>

<style lang="stylus">
body.highlight-notes-drawer--overflow {
  overflow: unset;
}

.editor-drawer.highlight-notes-drawer {
  --header-height: $nebula-space-7x;
  $max-width = 350px;
  width: $max-width;

  &-transition--enter-active {
    transition: all 0.25s ease-in-out;
  }
  &-transition--leave-active {
    transition: all 0.25s ease-in-out;
  }
  &-transition--enter-from,
  &-transition--leave-to {
    width: 0;
  }

  .notes-panel {
    height: 100%;
    width: 100%;

    > div:not([class]) {
      height: 100%;
      width: 100%;
    }

    &__title {
      font-size: $nebula-font-size-body-1;
      margin: 0;
    }

    &__wrapper {
      height: 100%;
      min-height: unset;
    }

    &__confirmation-modal-header,
    &__confirmation-modal-content {
      font-size: 100%;
      margin: 0;
    }

    &__notes-list {
      list-style-type: none;
      margin: 0;
      padding: 0;
    }

    &__avatar {
      height: 250px;
      width: 250px;
    }

    &__empty-heading,
    &__empty-text {
      margin: 0;
    }
  }

  .nebula-panel {
    height: 100%;
    max-width: $max-width;
    transition: none;
    width: 100%;

    &__top {
      height: var(--header-height);
    }

    &__bottom {
      height: calc(100% - var(--header-height));
    }
  }

  .note__warning-text {
    margin: 0;
  }

  .notes-panel {
    overflow: hidden;
  }

  .notes-panel__content {
    height: 100%;
    justify-content: unset;
    overflow: auto;
    padding: $nebula-space-2x $nebula-space-2x $nebula-space-1x $nebula-space-2x;
  }

  /*
    Force the highlight notes drawer to overlap the canvas on smaller
    screens sizes and disable animation.
  */
  @media (max-width: $nebula-breakpoints-tablet-portrait + 1px) {
    inset-inline-end: 0;
    position: absolute;
    transition: none;

    .nebula-panel {
      position: absolute;
      top: 0;
    }

    .notes-panel__content {
      height: calc(100% - v-bind(offsetBottom));
    }
  }

  /*
    For smaller screens, reduce the header size and padding to
    conserve available screen space.
  */
  @media (max-width: 480px) {
    --header-height: $nebula-space-5x;

    .notes-panel__content {
      padding: $nebula-space-1x;
    }

    .notes-panel__note {
      margin-block-end: $nebula-space-1x;
    }
  }
}
</style>
